import { BaseDecisionPriority, DecisionContext, DecisionResult, MoveInfo } from "..\/..\/interfaces\/DecisionPriority";
import { GameState, Position, PieceType, MoveType, EffectType } from "..\/..\/..\/..\/types";
import { cloneFullGameState, applyMoveWithSnakeRespawn } from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

export class EndgameKingAdvance extends BaseDecisionPriority {
  readonly priority = 28;
  readonly name = "Endgame King Advance";
  readonly description = "Prioritize safe king advancement in endgames to pursue race victory";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    const pieceCount = this.countPieces(context.gameState);
    return pieceCount.ourPieces <= 3 && pieceCount.enemyPieces <= 3; // Activate in very low-piece endgames
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    const kingAdvances: MoveInfo[] = [];

    const kingMoves = context.allPossibleMoves.filter(move => {
      const piece = context.gameState.board[move.fromPosition.row][move.fromPosition.col].Pieces[0];
      return piece?.isKing;
    });

    for (const kingMove of kingMoves) {
      if (this.isSafeAdvance(kingMove, context.gameState)) {
        kingAdvances.push(kingMove);
        this.analysisLogs.push(`✅ Safe king advance: From [${kingMove.fromPosition.row},${kingMove.fromPosition.col}] to [${kingMove.move.position.row},${kingMove.move.position.col}]`);
      }
    }

    if (kingAdvances.length > 0) {
      // Early exit with best safe advance
      const bestAdvance = this.selectBestAdvance(kingAdvances, context.gameState);
      this.analysisLogs.push(`🎯 Selected best king advance for race pursuit`);
      return this.createEarlyExitResult(bestAdvance, "Endgame king advance to pursue race victory", { logs: this.analysisLogs });
    }

    this.analysisLogs.push("❌ No safe king advances available in endgame");
    return this.createNoActionResult("No safe king advances");
  }

  private isSafeAdvance(moveInfo: MoveInfo, gameState: GameState): boolean {
    const testState = cloneFullGameState(gameState);
    const king = testState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col].Pieces[0];
    applyMoveWithSnakeRespawn(testState, moveInfo.fromPosition, moveInfo.move, king);

    // Check if king is now entangleable/capturable by enemy
    const enemyMoves = MCTSMoveGenerationService.getInstance().getAllMoves(testState);
    const newKingPos = moveInfo.move.position;
    return !enemyMoves.some(em => 
      em.move.position.row === newKingPos.row && 
      em.move.position.col === newKingPos.col && 
      (em.move.moveType === MoveType.capture || em.move.sideEffects?.some(se => se.effect.type === EffectType.entangle))
    );
  }

  private selectBestAdvance(advances: MoveInfo[], gameState: GameState): MoveInfo {
    // Select the advance that gets closest to victory row (simulation-based)
    const ourVictoryRow = gameState.isLightTurn ? 7 : 0;
    return advances.reduce((best, current) => {
      const bestDist = Math.abs(best.move.position.row - ourVictoryRow);
      const currDist = Math.abs(current.move.position.row - ourVictoryRow);
      return currDist < bestDist ? current : best;
    });
  }

  private countPieces(state: GameState): { ourPieces: number; enemyPieces: number } {
    let our = 0, enemy = 0;
    const ourColor = state.isLightTurn;
    state.board.forEach(row => row.forEach(square => {
      square.Pieces.forEach(piece => {
        if (piece.isLight === ourColor) our++;
        else enemy++;
      });
    }));
    return { ourPieces: our, enemyPieces: enemy };
  }
} 
